import React, { useState, useEffect, useRef } from 'react';
// Updated weight unit from kg to g (gram)
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Space,
  Row,
  Col,
  Switch,
  Upload,
  Table,
  Modal,
  Tag,
  Divider,
  message,
  DatePicker,
  Checkbox,
  Tabs,
  Image
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  UploadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { productService, categoryService, attributeService } from '../../services';
import API_CONFIG from '../../config/api';
import apiClient from '../../services/api';
import dayjs from 'dayjs';
import RichTextEditor from '../../components/RichTextEditor';

const { Option } = Select;
const { TextArea } = Input;

const ProductForm = () => {
  
  const [form] = Form.useForm();
  const [variantForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]);
  const [variants, setVariants] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [activeTab, setActiveTab] = useState('1');
  
  // Debug token trạng thái
  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    //console.log('🔍 ProductForm token check:', {
    //   hasAccessToken: !!accessToken,
    //   hasRefreshToken: !!refreshToken,
    //   accessTokenLength: accessToken ? accessToken.length : 0,
    //   refreshTokenLength: refreshToken ? refreshToken.length : 0,
    //   timestamp: new Date().toISOString()
    // });
  }, []);
  const [attributeValues, setAttributeValues] = useState([]);
  const [variantModalVisible, setVariantModalVisible] = useState(false);
  const [currentVariant, setCurrentVariant] = useState(null);
  const [previewImage, setPreviewImage] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [descriptionHtml, setDescriptionHtml] = useState('');
  const [showDescriptionPreview, setShowDescriptionPreview] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  // Modal styling
  const modalStyles = {
    attributeCard: {
      marginBottom: 16,
      border: '2px solid #1890ff',
      boxShadow: '0 4px 12px rgba(24, 144, 255, 0.15)'
    },
    infoCard: {
      marginBottom: 16,
      border: '1px solid #d9d9d9'
    },
    selectedPreview: {
      marginTop: 8,
      padding: 12,
      backgroundColor: '#f6ffed',
      border: '1px solid #b7eb8f',
      borderRadius: 6,
      transition: 'all 0.3s ease'
    },
    skuInput: {
      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
      fontWeight: 'bold',
      color: '#1890ff',
      backgroundColor: '#f0f8ff',
      border: '2px solid #1890ff'
    },
    modalFooter: {
      textAlign: 'right',
      marginTop: 24,
      borderTop: '1px solid #f0f0f0',
      paddingTop: 16
    }
  };

  // Utility function to generate SKU
  const generateSKU = (productName, selectedAttributeValues) => {
    // Convert product name to slug
    const productSlug = productName
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9\s]/g, '')      // Remove special chars
      .replace(/\s+/g, '-')              // Replace spaces with hyphens
      .substring(0, 10);                 // Limit length

    // Get attribute values and convert to slug
    const attrSlugs = selectedAttributeValues.map(valueId => {
      const attr = attributeValues.find(av => av.id === valueId);
      if (attr) {
        return attr.value
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]/g, '')
          .substring(0, 5);
      }
      return '';
    }).filter(slug => slug);

    // Combine: productname-attr1-attr2
    return [productSlug, ...attrSlugs].join('-').toUpperCase();
  };

  const handleAttributeValuesChange = (selectedValues) => {
    const productName = form.getFieldValue('name') || '';
    if (productName && selectedValues && selectedValues.length > 0) {
      const generatedSKU = generateSKU(productName, selectedValues);
      variantForm.setFieldsValue({
        sku: generatedSKU,
        attribute_value_ids: selectedValues
      });
    }
  };

  useEffect(() => {
    const initData = async () => {
      await loadCategories();
      await loadAttributes();
      if (isEditing) {
        await loadProduct(id);
      }
    };
    initData();
  }, [id, isEditing]);

  const loadCategories = async () => {
    try {
      //console.log('🏷️ Loading categories...');
      const response = await categoryService.getAll();
      //console.log('🏷️ Categories response:', response);
      if (response.success) {
        setCategories(response.data || []);
        //console.log('✅ Categories loaded successfully:', response.data?.length || 0);
      }
    } catch (error) {
      //console.error('❌ Error loading categories:', error);
      message.error('Không thể tải danh sách danh mục: ' + (error.message || 'Unknown error'));
    }
  };

  const loadAttributes = async () => {
    try {
      //console.log('🎛️ Loading attributes...');
      const response = await attributeService.getAll();
      //console.log('🎛️ Attributes response:', response);
      if (response.success) {
        setAttributes(response.data || []);
        //console.log('✅ Attributes loaded successfully:', response.data?.length || 0);
        
        // Flatten all attribute values for easy lookup and include attributeId
        const allValues = (response.data || []).reduce((acc, attr) => {
          return [...acc, ...attr.values.map(v => ({ ...v, attributeName: attr.name, attributeId: attr.id }))];
        }, []);
        setAttributeValues(allValues);
      }
    } catch (error) {
      //console.error('❌ Error loading attributes:', error);
      message.error('Không thể tải danh sách thuộc tính: ' + (error.message || 'Unknown error'));
    }
  };

  const loadProduct = async (productId) => {
    setLoading(true);
    try {
      const response = await productService.getById(productId);
      if (response && response.success && response.data) {
        const productData = response.data;

        // Set description HTML content
        if (productData.description) {
          setDescriptionHtml(productData.description);
        }

        // Set form values
        form.setFieldsValue({
          name: productData.name,
          description: productData.description,
          category_id: productData.category_id,
          price: parseFloat(productData.price) || 0,
          discount_price: productData.discount_price ? parseFloat(productData.discount_price) : null,
          promotion_start: productData.promotion_start ? dayjs(productData.promotion_start) : null,
          promotion_end: productData.promotion_end ? dayjs(productData.promotion_end) : null,
          is_featured: productData.is_featured === 1,
          is_new: productData.is_new === 1,
          is_best_seller: productData.is_best_seller === 1,
        });

        // Set images
        setImages(productData.images || []);

        // Set variants - map variant_attributes from product level to each variant
        const processedVariants = (productData.variants || []).map(variant => {
          // Get variant_attributes for this variant from product.variant_attributes
          const variantAttrs = (productData.variant_attributes || []).filter(
            attr => attr.variant_id === variant.id
          );
          
          const attrValueIds = variantAttrs.length > 0
            ? variantAttrs.map(va => va.attribute_value_id)
            : (variant.attribute_value_ids || []);
          
          return {
            ...variant,
            attribute_value_ids: attrValueIds,
            variant_attributes: variantAttrs
          };
        });
        
        setVariants(processedVariants)

        // Set selected attributes based on product attributes
        if (productData.attributes && productData.attributes.length > 0) {
          // Extract unique attribute IDs from product attributes
          const attributeIds = [...new Set(productData.attributes.map(attr => {
            // Find the attribute by name and get its ID
            const attrObj = attributes.find(a => a.name === attr.attribute_name);
            return attrObj ? attrObj.id : null;
          }).filter(id => id !== null))];

          form.setFieldsValue({
            selectedAttributes: attributeIds
          });
        }

        //console.log('✅ Product loaded successfully:', productData);
      } else {
        //console.error('❌ Invalid response format:', response);
        message.error('Dữ liệu sản phẩm không hợp lệ');
      }
    } catch (error) {
      //console.error('❌ Error loading product:', error);
      message.error('Không thể tải thông tin sản phẩm: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    
    // Hiển thị thông báo đặc biệt khi chuẩn bị gửi request
    const savingMessage = message.loading(
      isEditing ? 'Đang cập nhật sản phẩm...' : 'Đang thêm sản phẩm...', 
      0
    );

    try {
      // Prepare product data according to stored procedure format
      const productData = {
        name: values.name,
        description: descriptionHtml || values.description || '',
        category_id: values.category_id,
        price: values.price,
        discount_price: values.discount_price || null,
        promotion_start: values.promotion_start ? values.promotion_start.format('YYYY-MM-DD HH:mm:ss') : null,
        promotion_end: values.promotion_end ? values.promotion_end.format('YYYY-MM-DD HH:mm:ss') : null,
        is_featured: values.is_featured ? 1 : 0,
        is_new: values.is_new ? 1 : 0,
        is_best_seller: values.is_best_seller ? 1 : 0,
        images: images.map(img => ({
          url: img.url || img.image_url,
          is_thumbnail: img.is_thumbnail || 0
        })),
        attributes: values.selectedAttributes ? values.selectedAttributes.map(id => ({ id: parseInt(id) })) : [],
        variants: variants.map(variant => ({
            price: variant.price,
            sku: variant.sku,
            stock: (typeof variant.stock !== 'undefined') ? variant.stock : 0,
            discount_price: variant.discount_price || null,
            promotion_start: variant.promotion_start || null,
            promotion_end: variant.promotion_end || null,
            image_url: variant.image_url || null,
            weight: variant.weight || null,
            low_stock_alert: variant.low_stock_alert || 5,
            attribute_value_ids: variant.attribute_value_ids || []
          }))
      };

      // If there are no variants, include product-level stock so stored-proc can create a default variant
      if (!productData.variants || productData.variants.length === 0) {
        if (typeof values.stock !== 'undefined') {
          productData.stock = values.stock;
        }
      } else {
        // ensure no ambiguous product-level stock when variants exist
        delete productData.stock;
      }

      let response;
      try {
        if (isEditing) {
          // Khi edit: chỉ cập nhật thông tin cơ bản, không gửi variants
          const { variants, images, ...basicData } = productData;
          response = await productService.updateSimple(id, basicData);
        } else {
          // Khi thêm mới: gửi đầy đủ
          response = await productService.create(productData);
        }

        if (response.success) {
          savingMessage(); // Đóng loading message
          message.success(
            isEditing ? 'Cập nhật sản phẩm thành công' : 'Thêm sản phẩm thành công'
          );
          navigate('/products/list');
        } else {
          savingMessage(); // Đóng loading message
          message.error(response.message || 'Lưu sản phẩm thất bại');
        }
      } catch (apiError) {
        savingMessage(); // Đóng loading message
        console.error('❌ API Error details:', apiError);
        
        // Xử lý lỗi đơn giản - để interceptor tự động handle token refresh
        const errorMessage = apiError.response?.data?.message || apiError.message || 'Có lỗi xảy ra khi lưu sản phẩm';
        message.error(errorMessage);
      }
    } catch (error) {
      savingMessage(); // Đóng loading message nếu chưa đóng
      console.error('❌ Error saving product:', error);
      
      // Chỉ hiển thị error message nếu chưa được xử lý ở trên
      if (!error.response || error.response.status === 0) {
        // Network error hoặc lỗi không có response
        message.error('Lỗi kết nối. Vui lòng kiểm tra internet và thử lại.');
      }
      // Các lỗi HTTP đã được xử lý ở try block bên trong
    } finally {
      setLoading(false);
    }
  };

  const handleAddVariant = () => {
    const selectedAttrs = form.getFieldValue('selectedAttributes') || [];
    const productName = form.getFieldValue('name') || '';
    
    if (selectedAttrs.length === 0) {
      message.warning('Vui lòng chọn thuộc tính cho sản phẩm trước');
      return;
    }
    
    if (!productName.trim()) {
      message.warning('Vui lòng nhập tên sản phẩm trước');
      return;
    }

    setCurrentVariant(null);
    variantForm.resetFields();
    
    // Set default values and prepare form for selected attributes
    variantForm.setFieldsValue({
      price: form.getFieldValue('price') || 0,
      stock: 0,
      weight: null,
      low_stock_alert: 5,
      discount_price: form.getFieldValue('discount_price') || null,
    });
    
    setVariantModalVisible(true);
  };

  const handleEditVariant = (variant, index) => {
    setCurrentVariant({ ...variant, index });
    variantForm.setFieldsValue({
      ...variant,
      attribute_value_ids: variant.attribute_value_ids || [],
      weight: variant.weight !== undefined && variant.weight !== null ? variant.weight : null,
      low_stock_alert: variant.low_stock_alert !== undefined && variant.low_stock_alert !== null ? variant.low_stock_alert : 5,
      promotion_start: variant.promotion_start ? dayjs(variant.promotion_start) : null,
      promotion_end: variant.promotion_end ? dayjs(variant.promotion_end) : null,
    });
    setVariantModalVisible(true);
  };

  const handleSaveVariant = async (values) => {
    try {
      // Nếu đang sửa, merge giá trị cũ với giá trị mới để không mất các trường không thay đổi
      let oldVariant = {};
      if (currentVariant && currentVariant.index !== undefined) {
        oldVariant = variants[currentVariant.index] || {};
      }
      const variantData = {
        sku: values.sku,
        attribute_value_ids: values.attribute_value_ids || []
      };
      
      // So sánh và CHỈ thêm field nếu KHÁC giá trị cũ
      if (values.price !== oldVariant.price) variantData.price = values.price;
      if (values.stock !== oldVariant.stock) variantData.stock = values.stock;
      if (values.discount_price !== oldVariant.discount_price) variantData.discount_price = values.discount_price;
      if (values.weight !== oldVariant.weight) variantData.weight = values.weight;
      if (values.low_stock_alert !== oldVariant.low_stock_alert) variantData.low_stock_alert = values.low_stock_alert;
      
      const newPromotionStart = values.promotion_start ? values.promotion_start.format('YYYY-MM-DD HH:mm:ss') : null;
      const newPromotionEnd = values.promotion_end ? values.promotion_end.format('YYYY-MM-DD HH:mm:ss') : null;
      if (newPromotionStart !== oldVariant.promotion_start) variantData.promotion_start = newPromotionStart;
      if (newPromotionEnd !== oldVariant.promotion_end) variantData.promotion_end = newPromotionEnd;
      if (values.image_id !== oldVariant.image_id) variantData.image_id = values.image_id;

      if (isEditing) {
        // Trang edit: Gọi API ngay
        if (currentVariant && currentVariant.id) {
          // Update existing variant via API
          const response = await productService.updateVariant(currentVariant.id, variantData);
          if (response.success) {
            const newVariants = [...variants];
            newVariants[currentVariant.index] = { ...variantData, id: currentVariant.id };
            setVariants(newVariants);
            message.success('Cập nhật biến thể thành công');
          } else {
            message.error(response.message || 'Cập nhật biến thể thất bại');
            return;
          }
        } else {
          // Add new variant via API
          const response = await productService.addVariant(id, variantData);
          if (response.success) {
            const newVariant = { ...variantData, id: response.data?.variant_id };
            setVariants([...variants, newVariant]);
            message.success('Thêm biến thể thành công');
          } else {
            message.error(response.message || 'Thêm biến thể thất bại');
            return;
          }
        }
      } else {
        // Trang add: Lưu vào state, gửi khi submit form
        if (currentVariant && currentVariant.index !== undefined) {
          const newVariants = [...variants];
          newVariants[currentVariant.index] = variantData;
          setVariants(newVariants);
        } else {
          setVariants([...variants, variantData]);
        }
        message.success('Lưu biến thể thành công');
      }
      
      setVariantModalVisible(false);
    } catch (error) {
      console.error('Error saving variant:', error);
      console.error('Error response:', error.response?.data);
      message.error(error.response?.data?.message || 'Lưu biến thể thất bại');
    }
  };

  const handleDeleteVariant = async (index) => {
    const variant = variants[index];
    
    if (isEditing && variant.id) {
      // Nếu đang edit và variant có id, gọi API xóa
      try {
        const response = await productService.deleteVariant(variant.id);
        if (response.success) {
          const newVariants = variants.filter((_, i) => i !== index);
          setVariants(newVariants);
          message.success('Xóa biến thể thành công');
        } else {
          message.error(response.message || 'Xóa biến thể thất bại');
        }
      } catch (error) {
        console.error('Error deleting variant:', error);
        message.error(error.response?.data?.message || 'Xóa biến thể thất bại');
      }
    } else {
      // Nếu đang thêm mới hoặc variant chưa có id, chỉ xóa khỏi state
      const newVariants = variants.filter((_, i) => i !== index);
      setVariants(newVariants);
      message.success('Xóa biến thể thành công');
    }
  };

  // 🔄 REFRESH TOKEN HANDLER
  const handleRefreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        message.error('Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại.');
        navigate('/login');
        return false;
      }

      const response = await apiClient.post(`${API_CONFIG.BASE_URL}/api/auth/refresh-token`, {
        refreshToken
      });

      if (response.data.success && response.data.data.accessToken) {
        localStorage.setItem('accessToken', response.data.data.accessToken);
        return true;
      }
      
      message.error('Không thể làm mới phiên đăng nhập. Vui lòng đăng nhập lại.');
      navigate('/login');
      return false;
    } catch (error) {
      message.error('Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại.');
      navigate('/login');
      return false;
    }
  };

  // 🔥 API 1: THÊM ẢNH SẢN PHẨM
  const addProductImage = async (productId, imageUrl, isThumbnail = false) => {
    console.log('🚀 API CALL: POST /api/products/' + productId + '/images');
    
    try {
      const response = await apiClient.post(`${API_CONFIG.BASE_URL}/api/products/${productId}/images`, {
        accessToken: localStorage.getItem('accessToken'),
        imageUrl: imageUrl,
        isThumbnail: isThumbnail
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Thêm ảnh thất bại');
      }
    } catch (error) {
      if (error.response?.status === 401) {
        const refreshed = await handleRefreshToken();
        if (refreshed) return addProductImage(productId, imageUrl, isThumbnail);
      }
      throw error;
    }
  };

  // 🗑️ API 2: XÓA ẢNH SẢN PHẨM
  const deleteProductImage = async (imageId) => {
    console.log('🚀 API CALL: DELETE /api/products/images/' + imageId);
    
    try {
      const response = await apiClient.delete(`${API_CONFIG.BASE_URL}/api/products/images/${imageId}`, {
        data: {
          accessToken: localStorage.getItem('accessToken')
        }
      });

      if (response.data.success) {
        return true;
      } else {
        throw new Error(response.data.message || 'Xóa ảnh thất bại');
      }
    } catch (error) {
      if (error.response?.status === 401) {
        const refreshed = await handleRefreshToken();
        if (refreshed) return deleteProductImage(imageId);
      }
      throw error;
    }
  };

  // ⭐ API 3: ĐẶT ẢNH LÀM THUMBNAIL
  const setProductThumbnail = async (imageId) => {
    console.log('🚀 API CALL: PUT /api/products/images/' + imageId + '/thumbnail');
    
    try {
      const response = await apiClient.put(`${API_CONFIG.BASE_URL}/api/products/images/${imageId}/thumbnail`, {
        accessToken: localStorage.getItem('accessToken')
      });

      if (response.data.success) {
        return true;
      } else {
        throw new Error(response.data.message || 'Đặt thumbnail thất bại');
      }
    } catch (error) {
      if (error.response?.status === 401) {
        const refreshed = await handleRefreshToken();
        if (refreshed) return setProductThumbnail(imageId);
      }
      throw error;
    }
  };

  const handleImageUpload = async (info) => {
    console.log('🎯 handleImageUpload called:', info.file.status);
    
    if (info.file.status === 'uploading') return;
    
    if (info.file.status === 'done') {
      const url = info.file.response?.url;
      console.log('🔗 Image URL:', url);
      
      if (!url) {
        message.error('Không nhận được URL ảnh từ server');
        return;
      }
      
      try {
        if (isEditing && id) {
          console.log('🔥 CALLING addProductImage API...');
          const isThumbnail = images.length === 0;
          await addProductImage(id, url, isThumbnail);
          message.success('Đã thêm ảnh thành công!');
          // Reload images
          const response = await productService.getById(id);
          if (response.success) {
            setImages(response.data.images || []);
          }
        } else {
          const newImage = {
            id: Date.now(),
            url,
            is_thumbnail: images.length === 0 ? 1 : 0,
            name: info.file.name
          };
          setImages(prev => [...prev, newImage]);
          message.success('Tải ảnh thành công');
        }
      } catch (error) {
        console.error('❌ Error:', error);
        message.error(`Lỗi thêm ảnh: ${error.message}`);
      }
    } else if (info.file.status === 'error') {
      message.error('Tải ảnh thất bại');
    }
  };

  // Upload helper for variant modal: upload a selected file, then set image_url and inject into product gallery for preview
  const uploadVariantImage = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiClient.post('/api/uploads', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      const data = res.data;
      if (data.success && data.url) {
        // set image_url on the variant form
        variantForm.setFieldsValue({ image_url: data.url });
        // push into product images if not there yet
        setImages(prev => {
          if (prev.some(img => (img.url || img.image_url) === data.url)) return prev;
          const newImg = {
            id: Date.now(),
            url: data.url,
            is_thumbnail: prev.length === 0 ? 1 : 0,
          };
          return [...prev, newImg];
        });
        message.success('Đã tải hình cho biến thể');
      } else {
        message.error('Upload hình thất bại');
      }
    } catch (e) {
      //console.error(e);
      message.error('Upload hình thất bại');
    }
  };

  const handleRemoveImage = async (imageId) => {
    console.log('🎯 handleRemoveImage called with ID:', imageId);
    
    const imageToRemove = images.find(img => img.id === imageId);
    if (!imageToRemove) {
      message.error('Không tìm thấy ảnh');
      return;
    }
    
    try {
      if (isEditing && id && (imageToRemove.image_id || imageToRemove.id)) {
        const serverId = imageToRemove.image_id || imageToRemove.id;
        console.log('🔥 DELETE API with serverId:', serverId);
        await deleteProductImage(serverId);
        console.log('✅ Delete API completed');
        message.success('Đã xóa ảnh thành công!');
        // Reload images
        const response = await productService.getById(id);
        if (response.success) {
          setImages(response.data.images || []);
        }
      } else {
        setImages(prev => prev.filter(img => img.id !== imageId));
        message.success('Đã xóa ảnh khỏi danh sách.');
      }
    } catch (error) {
      console.error('❌ Error:', error);
      message.error(error.message);
    }
  };

  const handleSetThumbnail = async (imageId) => {
    console.log('🎯 handleSetThumbnail called with ID:', imageId);
    
    const imageToSet = images.find(img => img.id === imageId);
    if (!imageToSet) {
      message.error('Không tìm thấy ảnh');
      return;
    }
    
    try {
      setImages(prev => prev.map(img => ({
        ...img,
        is_thumbnail: img.id === imageId ? 1 : 0
      })));
      
      if (isEditing && id && (imageToSet.image_id || imageToSet.id)) {
        const serverId = imageToSet.image_id || imageToSet.id;
        console.log('🔥 SET THUMBNAIL API with serverId:', serverId);
        await setProductThumbnail(serverId);
        console.log('✅ Set thumbnail API completed');
        message.success('Đã đặt làm ảnh đại diện!');
        // Reload images
        const response = await productService.getById(id);
        if (response.success) {
          setImages(response.data.images || []);
        }
      } else {
        message.success('Đã đặt làm ảnh đại diện.');
      }
    } catch (error) {
      console.error('❌ Error:', error);
      message.error(error.message);
    }
  };

  const variantColumns = [
    {
      title: 'SKU',
      dataIndex: 'sku',
      width: 180,
      render: (sku) => (
        <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{sku}</span>
      ),
    },
    {
      title: 'Giá',
      dataIndex: 'price',
      render: (price) => price ? `${price.toLocaleString()}đ` : '-',
      width: 120,
    },
    {
      title: 'Tồn kho',
      dataIndex: 'stock',
      width: 80,
    },
    {
      title: 'Cảnh báo tồn kho',
      dataIndex: 'low_stock_alert',
      render: (value) => value || 5,
      width: 100,
    },
    {
      title: 'Trọng lượng',
      dataIndex: 'weight',
      render: (weight) => weight ? `${weight} g` : '-',
      width: 100,
    },
    {
      title: 'Thuộc tính',
      key: 'attributes',
      render: (_, record) => {
        // Try to get from variant_attributes first (from API)
        if (record.variant_attributes && record.variant_attributes.length > 0) {
          return (
            <div>
              {record.variant_attributes.map((attr, index) => (
                <Tag key={`${record.sku || record.id || index}-${attr.attribute_name}-${attr.attribute_value}`} color="blue" style={{ marginBottom: 4 }}>
                  {attr.attribute_name}: {attr.attribute_value}
                </Tag>
              ))}
            </div>
          );
        }
        
        // Fallback to attribute_value_ids (for new variants)
        const valueIds = record.attribute_value_ids || [];
        return (
          <div>
            {valueIds.map(valueId => {
              const attr = attributeValues.find(av => av.id === valueId);
              return attr ? (
                <Tag key={`${record.sku || record.id || 'unknown'}-${valueId}-${attr.value}`} color="blue" style={{ marginBottom: 4 }}>
                  {attr.attributeName}: {attr.value}
                </Tag>
              ) : (
                <Tag key={`${record.sku || record.id || 'unknown'}-missing-${valueId}-${Date.now()}`} color="red">
                  ID: {valueId} (không tìm thấy)
                </Tag>
              );
            })}
            {valueIds.length === 0 && (
              <span style={{ color: '#999' }}>Chưa chọn thuộc tính</span>
            )}
          </div>
        );
      },
      width: 200,
    },
    {
      title: 'Khuyến mãi',
      render: (_, record) => {
        if (record.discount_price) {
          return (
            <div>
              <div style={{ color: '#f50', fontWeight: 500 }}>
                {record.discount_price.toLocaleString()}đ
              </div>
              {record.promotion_start && record.promotion_end && (
                <div style={{ fontSize: '12px', color: '#999' }}>
                  {dayjs(record.promotion_start).format('DD/MM')} - {dayjs(record.promotion_end).format('DD/MM')}
                </div>
              )}
            </div>
          );
        }
        return <span style={{ color: '#999' }}>Không có</span>;
      },
      width: 120,
    },
    {
      title: 'Thao tác',
      render: (_, record, index) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditVariant(record, index)}
            title="Chỉnh sửa"
          />
          <Button
            icon={<DeleteOutlined />}
            size="small"
            danger
            onClick={() => handleDeleteVariant(index)}
            title="Xóa"
          />
        </Space>
      ),
      width: 100,
      fixed: 'right',
    },
  ];

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Tải ảnh</div>
    </div>
  );

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/products/list')}
            >
              Quay lại
            </Button>
            <h2>{isEditing ? 'Chỉnh sửa sản phẩm' : 'Thêm sản phẩm mới'}</h2>
          </Space>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: "1",
                label: "Thông tin cơ bản",
                children: (
                  <Row gutter={[24, 24]}>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        label="Tên sản phẩm"
                        name="name"
                        rules={[{ required: true, message: 'Vui lòng nhập tên sản phẩm' }]}
                      >
                        <Input placeholder="Nhập tên sản phẩm" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        label="Danh mục"
                        name="category_id"
                        rules={[{ required: true, message: 'Vui lòng chọn danh mục' }]}
                      >
                        <Select placeholder="Chọn danh mục">
                          {categories.map(category => (
                            <Option key={category.id} value={category.id}>
                              {category.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item
                        label={
                          <Space>
                            <span>Mô tả sản phẩm</span>
                            <Button 
                              type="link" 
                              size="small" 
                              icon={<EyeOutlined />}
                              onClick={() => setShowDescriptionPreview(!showDescriptionPreview)}
                            >
                              {showDescriptionPreview ? 'Chỉnh sửa' : 'Xem trước'}
                            </Button>
                          </Space>
                        }
                        name="description"
                      >
                        {showDescriptionPreview ? (
                          <div 
                            style={{
                              border: '1px solid #d9d9d9',
                              borderRadius: '6px',
                              padding: '16px',
                              minHeight: '200px',
                              background: '#fff',
                              fontSize: '14px',
                              wordBreak: 'break-word'
                            }}
                            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                          />
                        ) : (
                          <RichTextEditor
                            value={descriptionHtml}
                            onChange={(content) => {
                              setDescriptionHtml(content);
                              form.setFieldsValue({ description: content });
                            }}
                            placeholder="Nhập mô tả chi tiết sản phẩm. Bạn có thể định dạng văn bản, thêm hình ảnh, liên kết..."
                          />
                        )}
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item
                        label="Giá gốc"
                        name="price"
                        rules={[{ required: true, message: 'Vui lòng nhập giá' }]}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={value => value.replace(/\$\s?|(,*)/g, '')}
                          placeholder="Nhập giá sản phẩm"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item
                        label="Giá khuyến mãi"
                        name="discount_price"
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={value => value.replace(/\$\s?|(,*)/g, '')}
                          placeholder="Nhập giá khuyến mãi"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item
                        label="Tồn kho"
                        name="stock"
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          placeholder="Nhập số lượng tồn kho"
                          addonAfter="Sản phẩm"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                )
              },
              {
                key: "2",
                label: "Thuộc tính & Biến thể",
                children: (
                  <>
                    {/* Bước 1: Chọn thuộc tính sản phẩm */}
                    <Card size="small" style={{ marginBottom: 16 }}>
                      <h4>Bước 1: Chọn thuộc tính sản phẩm</h4>
                      <Form.Item
                        name="selectedAttributes"
                        tooltip="Chọn các thuộc tính mà sản phẩm này sẽ có"
                      >
                        <Checkbox.Group>
                          <Row>
                            {attributes.map(attr => (
                              <Col key={attr.id} xs={24} sm={12} md={8}>
                                <Checkbox value={attr.id}>{attr.name}</Checkbox>
                              </Col>
                            ))}
                          </Row>
                        </Checkbox.Group>
                      </Form.Item>

                      {/* Hiển thị thuộc tính đã chọn với giá trị */}
                      <Form.Item dependencies={['selectedAttributes']}>
                        {({ getFieldValue }) => {
                          const selectedAttrs = getFieldValue('selectedAttributes') || [];
                          return selectedAttrs.length > 0 ? (
                            <div style={{ marginTop: 16 }}>
                              <Divider />
                              <h5>Thuộc tính đã chọn:</h5>
                              {selectedAttrs.map(attrId => {
                                const attr = attributes.find(a => a.id === attrId);
                                return attr ? (
                                  <div key={attr.id} style={{ marginBottom: 12 }}>
                                    <strong>{attr.name}:</strong>
                                    <div style={{ marginTop: 4 }}>
                                      {attr.values.map(value => (
                                        <Tag key={value.id} color="blue" style={{ margin: 2 }}>
                                          {value.value}
                                        </Tag>
                                      ))}
                                    </div>
                                  </div>
                                ) : null;
                              })}
                            </div>
                          ) : null;
                        }}
                      </Form.Item>
                    </Card>

                    {/* Bước 2: Tạo biến thể */}
                    <Card size="small">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <h4 style={{ margin: 0 }}>Bước 2: Tạo biến thể sản phẩm</h4>
                        <Button
                          type="dashed"
                          icon={<PlusOutlined />}
                          onClick={handleAddVariant}
                        >
                          Thêm biến thể
                        </Button>
                      </div>

                      {variants.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                          <p>Chưa có biến thể nào. Hãy chọn thuộc tính và tên sản phẩm trước, sau đó click "Thêm biến thể".</p>
                        </div>
                      ) : (
                        <Table
                          columns={variantColumns}
                          dataSource={variants}
                          rowKey={record => record.sku || record.id || String(Math.random())}
                          size="small"
                          pagination={false}
                          scroll={{ x: 1000 }}
                        />
                      )}
                    </Card>
                  </>
                )
              },
              {
                key: "3",
                label: "Hình ảnh",
                children: (
                  <div style={{ marginBottom: 16 }}>
                    <h4>Hình ảnh sản phẩm</h4>
                    <Row gutter={[16, 16]}>
                      {images.map((image, index) => (
                        <Col key={image.id} xs={12} sm={8} md={6} lg={4}>
                          <div style={{ position: 'relative', border: '1px solid #d9d9d9', borderRadius: 4 }}>
                            <Image
                              width="100%"
                              height={120}
                              src={image.url || image.image_url}
                              style={{ objectFit: 'cover' }}
                              preview={{
                                onVisibleChange: setPreviewVisible,
                              }}
                            />
                            {image.is_thumbnail === 1 && (
                              <Tag
                                color="gold"
                                style={{
                                  position: 'absolute',
                                  top: 4,
                                  left: 4,
                                  fontSize: '10px'
                                }}
                              >
                                Đại diện
                              </Tag>
                            )}
                            <div style={{ 
                              position: 'absolute', 
                              top: 4, 
                              right: 4,
                              display: 'flex',
                              gap: 4
                            }}>
                              <Button
                                size="small"
                                type="primary"
                                ghost
                                onClick={() => handleSetThumbnail(image.id)}
                                disabled={image.is_thumbnail === 1}
                              >
                                Đại diện
                              </Button>
                              <Button
                                icon={<DeleteOutlined />}
                                size="small"
                                danger
                                onClick={() => handleRemoveImage(image.id)}
                              />
                            </div>
                          </div>
                        </Col>
                      ))}
                      <Col xs={12} sm={8} md={6} lg={4}>
                        <Upload
                          listType="picture-card"
                          showUploadList={false}
                          action={API_CONFIG.CURRENT_URL + '/api/uploads'}
                          name="file"
                          onChange={handleImageUpload}
                          multiple
                          style={{ width: '100%', height: 120 }}
                        >
                          {uploadButton}
                        </Upload>
                      </Col>
                    </Row>
                  </div>
                )
              },
              {
                key: "4",
                label: "Khuyến mãi & Nhãn",
                children: (
                  <Row gutter={[24, 24]}>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        label="Thời gian bắt đầu khuyến mãi"
                        name="promotion_start"
                      >
                        <DatePicker
                          showTime
                          style={{ width: '100%' }}
                          placeholder="Chọn thời gian bắt đầu"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        label="Thời gian kết thúc khuyến mãi"
                        name="promotion_end"
                      >
                        <DatePicker
                          showTime
                          style={{ width: '100%' }}
                          placeholder="Chọn thời gian kết thúc"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Divider />
                      <h4>Nhãn sản phẩm</h4>
                      <Space direction="vertical">
                        <Form.Item name="is_featured" valuePropName="checked" style={{ margin: 0 }}>
                          <Switch checkedChildren="Nổi bật" unCheckedChildren="Bình thường" />
                        </Form.Item>
                        <Form.Item name="is_new" valuePropName="checked" style={{ margin: 0 }}>
                          <Switch checkedChildren="Mới" unCheckedChildren="Cũ" />
                        </Form.Item>
                        <Form.Item name="is_best_seller" valuePropName="checked" style={{ margin: 0 }}>
                          <Switch checkedChildren="Bán chạy" unCheckedChildren="Bình thường" />
                        </Form.Item>
                      </Space>
                    </Col>
                  </Row>
                )
              }
            ]}
          />

          <Divider />
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => navigate('/products/list')}>
                Hủy
              </Button>
              {!isEditing && (
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                  disabled={loading}
                >
                  {loading ? 'Đang thêm sản phẩm...' : 'Thêm sản phẩm'}
                </Button>
              )}
              {isEditing && activeTab !== '2' && (
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                  disabled={loading}
                >
                  {loading ? 'Đang cập nhật...' : 'Cập nhật thông tin'}
                </Button>
              )}
              {isEditing && activeTab === '2' && (
                <Button
                  type="primary"
                  onClick={() => navigate('/products/list')}
                >
                  Hoàn thành
                </Button>
              )}
            </Space>
          </div>
        </Form>
      </Card>

      {/* Variant Modal */}
      <Modal
        title={
          <div>
            <span>{currentVariant ? 'Chỉnh sửa biến thể' : 'Thêm biến thể mới'}</span>
            <div style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginTop: 4 }}>
              Chọn giá trị thuộc tính để tự động sinh SKU
            </div>
          </div>
        }
        open={variantModalVisible}
        onCancel={() => setVariantModalVisible(false)}
        footer={null}
        width={900}
        destroyOnHidden={true}
      >
        <Form
          form={variantForm}
          layout="vertical"
          onFinish={handleSaveVariant}
        >
          {/* Phần chọn thuộc tính - quan trọng nhất */}
          <Card 
            size="small" 
            title="1. Chọn giá trị thuộc tính" 
            style={modalStyles.attributeCard}
          >
            <Form.Item
              name="attribute_value_ids"
              rules={[
                { required: true, message: 'Vui lòng chọn ít nhất một giá trị thuộc tính' },
                { 
                  type: 'array', 
                  min: 1, 
                  message: 'Phải chọn ít nhất một giá trị thuộc tính' 
                }
              ]}
              tooltip="Chọn giá trị cụ thể cho từng thuộc tính của biến thể này. SKU sẽ tự động sinh dựa trên lựa chọn của bạn."
            >
              <Select
                mode="multiple"
                placeholder="Chọn giá trị thuộc tính (VD: Màu đỏ, Size L)..."
                style={{ width: '100%' }}
                onChange={handleAttributeValuesChange}
                optionFilterProp="children"
                size="large"
              >
                {(() => {
                  const selectedAttrs = form.getFieldValue('selectedAttributes') || [];
                  const groupedOptions = {};

                  // Group options by attributeId (safer than matching by name)
                  attributeValues
                    .filter(av => selectedAttrs.includes(av.attributeId))
                    .forEach(av => {
                      if (!groupedOptions[av.attributeId]) {
                        // find parent attribute name for label
                        const parent = attributes.find(a => a.id === av.attributeId);
                        groupedOptions[av.attributeId] = {
                          name: parent ? parent.name : av.attributeName || String(av.attributeId),
                          values: []
                        };
                      }
                      groupedOptions[av.attributeId].values.push(av);
                    });

                  // Render grouped options
                  return Object.entries(groupedOptions).map(([attrId, group]) => (
                    <Select.OptGroup key={attrId} label={group.name}>
                      {group.values.map(av => (
                        <Option key={av.id} value={av.id}>
                          <Tag color="blue" style={{ marginRight: 8 }}>
                            {group.name}
                          </Tag>
                          {av.value}
                        </Option>
                      ))}
                    </Select.OptGroup>
                  ));
                })()}
              </Select>
            </Form.Item>
            
            {/* Display selected attributes preview */}
            <Form.Item dependencies={['attribute_value_ids']}>
              {({ getFieldValue }) => {
                const selectedValueIds = getFieldValue('attribute_value_ids') || [];
                if (selectedValueIds.length === 0) return null;
                
                return (
                  <div style={modalStyles.selectedPreview}>
                    <div style={{ fontSize: '12px', color: '#52c41a', marginBottom: 4 }}>
                      ✅ Thuộc tính đã chọn:
                    </div>
                    <div>
                      {selectedValueIds.map(valueId => {
                        const attr = attributeValues.find(av => av.id === valueId);
                        return attr ? (
                          <Tag key={valueId} color="green" style={{ margin: 2 }}>
                            {attr.attributeName}: {attr.value}
                          </Tag>
                        ) : null;
                      })}
                    </div>
                  </div>
                );
              }}
            </Form.Item>
          </Card>

          {/* Phần thông tin biến thể */}
          <Card size="small" title="2. Thông tin biến thể" style={modalStyles.infoCard}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="SKU (Tự động sinh)"
                  name="sku"
                  rules={[{ required: true, message: 'SKU là bắt buộc' }]}
                  tooltip="SKU sẽ được tự động tạo dựa trên tên sản phẩm và thuộc tính đã chọn"
                >
                  <Input 
                    placeholder="SKU sẽ được tự động tạo khi chọn thuộc tính..." 
                    style={modalStyles.skuInput}
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Giá bán"
                  name="price"
                  rules={[{ required: true, message: 'Vui lòng nhập giá bán' }]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                    placeholder="Nhập giá bán"
                    size="large"
                    min={0}
                    addonAfter="VNĐ"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Số lượng tồn kho"
                  name="stock"
                  rules={[{ required: true, message: 'Vui lòng nhập số lượng tồn kho' }]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    placeholder="Nhập số lượng tồn kho"
                    size="large"
                    addonAfter="Sản phẩm"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Giá khuyến mãi"
                  name="discount_price"
                  tooltip="Để trống nếu không có khuyến mãi"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                    placeholder="Nhập giá khuyến mãi (tùy chọn)"
                    size="large"
                    min={0}
                    addonAfter="VNĐ"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Trọng lượng (g)"
                  name="weight"
                  tooltip="Trọng lượng của biến thể này (tính bằng gram)"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    step={1}
                    placeholder="Ví dụ: 1500"
                    size="large"
                    addonAfter="g"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Ngưỡng cảnh báo tồn kho"
                  name="low_stock_alert"
                  tooltip="Số lượng tồn kho tối thiểu để hệ thống gửi cảnh báo"
                  initialValue={5}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    placeholder="Mặc định: 5"
                    size="large"
                    addonAfter="Sản phẩm"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Phần tùy chọn thêm */}
          <Card size="small" title="3. Thông tin bổ sung (Tùy chọn)" style={modalStyles.infoCard}>
            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <Form.Item
                  label="URL hình ảnh riêng"
                  name="image_url"
                  tooltip="Hình ảnh riêng cho biến thể này (nếu khác với hình ảnh chung của sản phẩm)"
                >
                  <Input 
                    placeholder="https://example.com/image.jpg"
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Upload
                  showUploadList={false}
                  beforeUpload={(file) => { uploadVariantImage(file); return false; }}
                >
                  <Button icon={<UploadOutlined />}>Tải ảnh cho biến thể (tự đặt vào hình chung)</Button>
                </Upload>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Thời gian bắt đầu khuyến mãi"
                  name="promotion_start"
                  dependencies={['discount_price']}
                >
                  <DatePicker
                    showTime
                    style={{ width: '100%' }}
                    placeholder="Chọn thời gian bắt đầu"
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Thời gian kết thúc khuyến mãi"
                  name="promotion_end"
                  dependencies={['discount_price']}
                >
                  <DatePicker
                    showTime
                    style={{ width: '100%' }}
                    placeholder="Chọn thời gian kết thúc"
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
          
          <div style={modalStyles.modalFooter}>
            <Space size="middle">
              <Button size="large" onClick={() => setVariantModalVisible(false)}>
                Hủy bỏ
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                size="large"
                icon={<SaveOutlined />}
              >
                {currentVariant ? 'Cập nhật biến thể' : 'Thêm biến thể'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductForm;
